import { useEffect, useMemo, useState, useCallback } from "react";
import {
  Loader2,
  Check,
  ChevronsUpDown,
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  Settings2,
} from "lucide-react";
import { toast } from "sonner";
import { useStores } from "@/stores";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { ComponentErrorBoundary } from "@/components/error/ComponentErrorBoundary";
import type { DocumentCategoryRow } from "@/stores/admin/models";
import { listDocumentCategories } from "@/stores/admin/repository";
import type { FileTypeFork } from "@/stores/admin/repository";

interface ManageCategorySubcategoriesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  parentCategory: DocumentCategoryRow | null;
}

interface EditableSubcategory {
  id?: number;
  category_name: string;
  description: string | null;
  category_type?: string;
}

interface SubcategoryOption {
  id: number;
  category_name: string;
  parent_category_id: number | null;
  parent_name?: string;
}

// Base fork properties shared by both new and existing forks
interface BaseFork {
  fork_name: string;
  file_types: string[];
  priority: number;
  subcategory_ids: number[];
}

// New fork (not yet saved to database)
interface NewFork extends BaseFork {
  id?: undefined;
  isNew: true;
}

// Existing fork (already saved to database)
interface ExistingFork extends BaseFork {
  id: number;
  isNew?: false;
}

// Discriminated union for type-safe fork handling
type EditableFork = NewFork | ExistingFork;

// Type guards for fork discrimination
function isExistingFork(fork: EditableFork): fork is ExistingFork {
  return typeof fork.id === "number" && fork.isNew !== true;
}

function isNewFork(fork: EditableFork): fork is NewFork {
  return fork.isNew === true || fork.id === undefined;
}

// Navigation state for atomic updates (prevents race conditions)
interface NavigationState {
  activeParent: DocumentCategoryRow | null;
  parentTrail: DocumentCategoryRow[];
}

// Snapshot an MST node into a plain object so it survives store.clear() calls.
// Uses JSON round-trip because spread ({...cat}) still triggers MST property getters
// which fail on dead nodes.
function snapshotCategory(cat: DocumentCategoryRow): DocumentCategoryRow {
  return JSON.parse(JSON.stringify(cat));
}

function parseFileTypes(input: string): string[] {
  const raw = input
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean)
    .map((t) => t.replace(/^\./, "")) // allow ".pdf"
    .map((t) => t.toLowerCase());

  if (raw.length === 0) return ["ALL"];
  if (raw.some((t) => t === "all")) return ["ALL"];
  return raw;
}

function formatFileTypes(fileTypes: string[]): string {
  if (!fileTypes || fileTypes.length === 0) return "ALL";
  if (fileTypes.some((t) => (t || "").toLowerCase() === "all")) return "ALL";
  return fileTypes.join(", ");
}

export function ManageCategorySubcategoriesDialog({
  open,
  onOpenChange,
  parentCategory,
}: ManageCategorySubcategoriesDialogProps) {
  const { documentCategoriesStore } = useStores();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // Combined navigation state for atomic updates (WHA-1147 fix)
  const [navigation, setNavigation] = useState<NavigationState>({
    activeParent: parentCategory,
    parentTrail: [],
  });
  const [subcategories, setSubcategories] = useState<EditableSubcategory[]>([]);
  const [subcategoryRows, setSubcategoryRows] = useState<DocumentCategoryRow[]>([]);
  const [originalSubcategories, setOriginalSubcategories] = useState<EditableSubcategory[]>([]);
  const [originalSubcategoryIds, setOriginalSubcategoryIds] = useState<number[]>([]);
  const [fileTypesInput, setFileTypesInput] = useState("ALL");
  const [originalFileTypesInput, setOriginalFileTypesInput] = useState("ALL");

  // Shared subcategories state
  const [sharedSubcategoryIds, setSharedSubcategoryIds] = useState<number[]>([]);
  const [originalSharedIds, setOriginalSharedIds] = useState<number[]>([]);
  const [allSubcategories, setAllSubcategories] = useState<SubcategoryOption[]>([]);
  const [sharedPopoverOpen, setSharedPopoverOpen] = useState(false);
  const [addExistingPopoverOpen, setAddExistingPopoverOpen] = useState(false);
  const [allCategoriesForAdopting, setAllCategoriesForAdopting] = useState<DocumentCategoryRow[]>(
    []
  );

  // File type forks state
  const [forks, setForks] = useState<EditableFork[]>([]);
  const [originalForks, setOriginalForks] = useState<EditableFork[]>([]);
  const [originalForkIds, setOriginalForkIds] = useState<number[]>([]);
  const [forksExpanded, setForksExpanded] = useState(true);

  // Filter available subcategories (exclude own children and self)
  const availableForSharing = useMemo(() => {
    if (!navigation.activeParent) return [];
    const ownChildIds = subcategories
      .filter((s) => typeof s.id === "number")
      .map((s) => s.id as number);
    return allSubcategories.filter(
      (sub) =>
        sub.id !== navigation.activeParent!.id && // not self
        sub.parent_category_id !== navigation.activeParent!.id && // not own child
        !ownChildIds.includes(sub.id) // extra safety
    );
  }, [navigation.activeParent, allSubcategories, subcategories]);

  // Available categories for adopting as direct subcategories (all categories except self and already-linked)
  const availableForAdopting = useMemo(() => {
    if (!navigation.activeParent) return [];
    const existingIds = new Set(
      subcategories.filter((s) => typeof s.id === "number").map((s) => s.id as number)
    );
    return allCategoriesForAdopting.filter(
      (cat) => cat.id !== navigation.activeParent!.id && !existingIds.has(cat.id)
    );
  }, [navigation.activeParent, allCategoriesForAdopting, subcategories, sharedSubcategoryIds]);

  // All available subcategories for fork selection (direct children + shared)
  const availableForForks = useMemo(() => {
    if (!navigation.activeParent) return [];
    // Direct children
    const directChildren = subcategories
      .filter((s) => typeof s.id === "number")
      .map((s) => ({
        id: s.id as number,
        category_name: s.category_name,
        parent_category_id: navigation.activeParent!.id,
        parent_name: navigation.activeParent!.category_name,
      }));
    // Shared subcategories
    const shared = sharedSubcategoryIds
      .map((id) => allSubcategories.find((s) => s.id === id))
      .filter((s): s is SubcategoryOption => s !== undefined);
    return [...directChildren, ...shared];
  }, [subcategories, sharedSubcategoryIds, allSubcategories, navigation.activeParent]);

  // Reset navigation state when dialog opens/closes (WHA-1147 fix: atomic state update)
  useEffect(() => {
    if (!open) {
      setNavigation({ activeParent: null, parentTrail: [] });
      return;
    }

    if (parentCategory) {
      const snapshot = snapshotCategory(parentCategory);
      setNavigation({ activeParent: snapshot, parentTrail: [snapshot] });
    }
  }, [open, parentCategory]);

  const normalizedFileTypesInput = useMemo(
    () => formatFileTypes(parseFileTypes(fileTypesInput)),
    [fileTypesInput]
  );

  const hasUnsavedChanges = useMemo(() => {
    const normalizeSubcategories = (items: EditableSubcategory[]) =>
      items.map((s) => ({
        id: s.id ?? null,
        category_name: s.category_name.trim(),
        description: (s.description ?? "").trim() || null,
      }));

    const normalizeForks = (items: EditableFork[]) =>
      items.map((f) => ({
        id: f.id ?? null,
        fork_name: f.fork_name.trim(),
        file_types: (f.file_types || [])
          .map((t) => t.trim().replace(/^\./, "").toLowerCase())
          .filter(Boolean)
          .sort(),
        priority: Number.isFinite(f.priority) ? f.priority : 100,
        subcategory_ids: [...(f.subcategory_ids || [])].sort(),
      }));

    const sharedSorted = [...sharedSubcategoryIds].sort();
    const sharedOriginalSorted = [...originalSharedIds].sort();

    if (
      JSON.stringify(normalizeSubcategories(subcategories)) !==
      JSON.stringify(normalizeSubcategories(originalSubcategories))
    ) {
      return true;
    }
    if (normalizedFileTypesInput !== originalFileTypesInput) {
      return true;
    }
    if (JSON.stringify(sharedSorted) !== JSON.stringify(sharedOriginalSorted)) {
      return true;
    }
    if (JSON.stringify(normalizeForks(forks)) !== JSON.stringify(normalizeForks(originalForks))) {
      return true;
    }
    return false;
  }, [
    subcategories,
    originalSubcategories,
    normalizedFileTypesInput,
    originalFileTypesInput,
    sharedSubcategoryIds,
    originalSharedIds,
    forks,
    originalForks,
  ]);

  const ensureNoUnsavedChanges = () => {
    if (!hasUnsavedChanges) return true;
    toast.error("Please save your changes before navigating.");
    return false;
  };

  // WHA-1147 fix: atomic navigation state updates
  const handleNavigateBack = useCallback(() => {
    if (!ensureNoUnsavedChanges()) return;
    setNavigation((prev) => {
      if (prev.parentTrail.length <= 1) return prev;
      const nextTrail = prev.parentTrail.slice(0, -1);
      return {
        activeParent: nextTrail[nextTrail.length - 1] ?? null,
        parentTrail: nextTrail,
      };
    });
  }, [ensureNoUnsavedChanges]);

  const handleManageSubcategory = useCallback(
    (subcategoryId: number) => {
      if (!ensureNoUnsavedChanges()) return;
      const row = subcategoryRows.find((r) => r.id === subcategoryId);
      if (!row) {
        toast.error("Unable to load this subcategory.");
        return;
      }
      const snapshot = snapshotCategory(row);
      setNavigation((prev) => ({
        activeParent: snapshot,
        parentTrail: [...prev.parentTrail, snapshot],
      }));
    },
    [ensureNoUnsavedChanges, subcategoryRows]
  );

  // WHA-1148 fix: improved async cancellation pattern
  useEffect(() => {
    if (!open || !navigation.activeParent) return;

    let cancelled = false;
    const currentParentId = navigation.activeParent.id;

    const load = async () => {
      if (cancelled) return;
      setIsLoading(true);
      try {
        // Load all data in parallel
        const [subRows, fileTypes, sharedIds, allSubs, forksData] = await Promise.all([
          documentCategoriesStore.loadSubcategories(currentParentId),
          documentCategoriesStore.loadCategorySubcategoryRuleFileTypes(currentParentId),
          documentCategoriesStore.loadSharedSubcategories(currentParentId),
          documentCategoriesStore.loadAllSubcategories(),
          documentCategoriesStore.loadFileTypeForks(currentParentId),
        ]);

        // Load all categories (including top-level) for the "Link existing" dropdown
        // Uses repo directly to avoid clearing the store's categories array (which would kill MST node refs)
        const allCats = await listDocumentCategories();

        // Check cancellation after async operations complete
        if (cancelled) return;

        setAllCategoriesForAdopting(allCats);

        const rows = (subRows || []) as DocumentCategoryRow[];
        setSubcategoryRows(rows);
        setOriginalSubcategoryIds(rows.map((r) => Number(r.id)));
        setSubcategories(
          rows.map((row) => ({
            id: Number(row.id),
            category_name: row.category_name,
            description: row.description ?? null,
            category_type: row.category_type,
          }))
        );
        const normalizedFileTypes = formatFileTypes(fileTypes || ["ALL"]);
        setFileTypesInput(normalizedFileTypes);
        setOriginalFileTypesInput(normalizedFileTypes);
        setOriginalSubcategories(
          rows.map((row) => ({
            id: Number(row.id),
            category_name: row.category_name,
            description: row.description ?? null,
            category_type: row.category_type,
          }))
        );

        // Shared subcategories
        const sharedIdList = (sharedIds || []) as number[];
        setSharedSubcategoryIds(sharedIdList);
        setOriginalSharedIds(sharedIdList);

        // All subcategories with parent info for display
        // Merge subcategories (have parent) with top-level categories so that
        // top-level categories shared as subcategories are visible in the UI.
        const allSubRows = (allSubs || []) as DocumentCategoryRow[];
        const allSubIds = new Set(allSubRows.map((r) => r.id));
        const topLevelRows = allCats.filter((r) => !allSubIds.has(r.id));
        const mergedRows = [...allSubRows, ...topLevelRows];
        const parentMap = new Map<number, string>();
        documentCategoriesStore.categoriesList.forEach((cat) => {
          parentMap.set(cat.id, cat.category_name);
        });
        setAllSubcategories(
          mergedRows.map((row) => ({
            id: row.id,
            category_name: row.category_name,
            parent_category_id: row.parent_category_id,
            parent_name: row.parent_category_id
              ? parentMap.get(row.parent_category_id) || "Unknown"
              : undefined,
          }))
        );

        // File type forks - map to EditableFork type
        const forksWithIds = (forksData || []) as (FileTypeFork & { subcategory_ids: number[] })[];
        setOriginalForkIds(forksWithIds.map((f) => f.id));
        const mappedForks: EditableFork[] = forksWithIds.map((f) => ({
          id: f.id,
          fork_name: f.fork_name || "",
          file_types: f.file_types || ["ALL"],
          priority: f.priority ?? 100,
          subcategory_ids: f.subcategory_ids || [],
        }));
        setForks(mappedForks);
        setOriginalForks(mappedForks);
      } catch (e) {
        // Only show error if not cancelled
        if (!cancelled) {
          toast.error("Failed to load subcategory configuration");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open, navigation.activeParent, documentCategoriesStore]);

  const handleSave = async () => {
    if (!navigation.activeParent) return;
    const activeParent = navigation.activeParent;

    setIsSaving(true);
    try {
      const parsedFileTypes = parseFileTypes(fileTypesInput);

      // 1. Save subcategories
      const cleaned = subcategories
        .map((s) => ({
          ...s,
          category_name: s.category_name.trim(),
          description: s.description?.trim() || null,
        }))
        .filter((s) => s.category_name.length > 0);

      const currentIds = cleaned
        .map((s) => s.id)
        .filter((id): id is number => typeof id === "number" && !Number.isNaN(id));
      const toDelete = originalSubcategoryIds.filter((id) => !currentIds.includes(id));
      const toUpdate = cleaned.filter((s) => typeof s.id === "number") as Array<
        EditableSubcategory & { id: number }
      >;
      const toCreate = cleaned.filter((s) => s.id == null);

      await Promise.all([
        ...toDelete.map((id) => documentCategoriesStore.deleteSubcategory(id)),
        ...toUpdate.map((s) =>
          documentCategoriesStore.updateSubcategory(s.id, {
            category_name: s.category_name,
            description: s.description,
            parent_category_id: activeParent.id,
            category_type: s.category_type || activeParent.category_type,
          })
        ),
        ...toCreate.map((s) =>
          documentCategoriesStore.createSubcategory({
            category_name: s.category_name,
            description: s.description,
            category_type: s.category_type || activeParent.category_type,
            parent_category_id: activeParent.id,
          })
        ),
      ]);

      // 2. Save file type rules (gate)
      await documentCategoriesStore.setCategorySubcategoryRuleFileTypes(
        activeParent.id,
        parsedFileTypes
      );

      // 3. Save shared subcategories if changed
      const sharedChanged =
        sharedSubcategoryIds.length !== originalSharedIds.length ||
        sharedSubcategoryIds.some((id) => !originalSharedIds.includes(id));
      if (sharedChanged) {
        await documentCategoriesStore.setSharedSubcategories(activeParent.id, sharedSubcategoryIds);
      }

      // 4. Save file type forks (WHA-1149 fix: using type guards for type safety)
      const currentForkIds = forks.filter(isExistingFork).map((f) => f.id);
      const forksToDelete = originalForkIds.filter((id) => !currentForkIds.includes(id));
      const forksToUpdate = forks.filter(isExistingFork);
      const forksToCreate = forks.filter(isNewFork);

      // Delete removed forks
      await Promise.all(forksToDelete.map((id) => documentCategoriesStore.deleteFileTypeFork(id)));

      // Update existing forks
      for (const fork of forksToUpdate) {
        await documentCategoriesStore.updateFileTypeFork(fork.id, {
          fork_name: fork.fork_name || null,
          file_types: fork.file_types,
          priority: fork.priority,
        });
        await documentCategoriesStore.setForkSubcategories(fork.id, fork.subcategory_ids);
      }

      // Create new forks
      for (const fork of forksToCreate) {
        const newFork = await documentCategoriesStore.createFileTypeFork(
          activeParent.id,
          fork.file_types,
          fork.fork_name || null,
          fork.priority
        );
        if (newFork && fork.subcategory_ids.length > 0) {
          await documentCategoriesStore.setForkSubcategories(newFork.id, fork.subcategory_ids);
        }
      }

      toast.success("Subcategory configuration saved");
      onOpenChange(false);
    } catch (e) {
      toast.error("Failed to save subcategory configuration");
    } finally {
      setIsSaving(false);
    }
  };

  const toggleSharedSubcategory = (id: number) => {
    setSharedSubcategoryIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const getSubcategoryLabel = (sub: SubcategoryOption) => {
    return sub.parent_name ? `${sub.category_name} (from ${sub.parent_name})` : sub.category_name;
  };

  const addFork = () => {
    setForks((prev) => [
      ...prev,
      {
        fork_name: "",
        file_types: ["ALL"],
        priority: (prev.length + 1) * 10,
        subcategory_ids: [],
        isNew: true,
      },
    ]);
  };

  const removeFork = (index: number) => {
    setForks((prev) => prev.filter((_, i) => i !== index));
  };

  const updateFork = (
    index: number,
    updates: Partial<
      Pick<EditableFork, "fork_name" | "file_types" | "priority" | "subcategory_ids">
    >
  ) => {
    setForks((prev) => prev.map((f, i) => (i === index ? { ...f, ...updates } : f)));
  };

  const toggleForkSubcategory = (forkIndex: number, subcategoryId: number) => {
    setForks((prev) =>
      prev.map((f, i) => {
        if (i !== forkIndex) return f;
        const ids = f.subcategory_ids.includes(subcategoryId)
          ? f.subcategory_ids.filter((id) => id !== subcategoryId)
          : [...f.subcategory_ids, subcategoryId];
        return { ...f, subcategory_ids: ids };
      })
    );
  };

  const disabled = isLoading || isSaving || !navigation.activeParent;
  const breadcrumbLabel = navigation.parentTrail.map((parent) => parent.category_name).join(" / ");

  // WHA-1151 fix: Reset handler for error boundary recovery
  const handleErrorReset = useCallback(() => {
    // Reset navigation to initial state to allow retry
    const snapshot = parentCategory ? snapshotCategory(parentCategory) : null;
    setNavigation({
      activeParent: snapshot,
      parentTrail: snapshot ? [snapshot] : [],
    });
  }, [parentCategory]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* WHA-1151 fix: Error boundary to catch render errors gracefully */}
      <ComponentErrorBoundary
        fallbackMessage="Failed to load subcategory management. Please try again."
        onRetry={handleErrorReset}
      >
        <DialogContent className="flex max-h-[90vh] flex-col sm:max-w-[750px]">
          <DialogHeader>
            <DialogTitle>Manage Subcategories</DialogTitle>
            <DialogDescription>
              Configure second-pass refinement for{" "}
              <span className="font-medium">{navigation.activeParent?.category_name ?? ""}</span>.
            </DialogDescription>
            {navigation.parentTrail.length > 1 && (
              <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleNavigateBack}
                  disabled={disabled}
                  className="h-7 px-2 text-xs"
                >
                  <ChevronLeft className="mr-1 h-3.5 w-3.5" />
                  Back
                </Button>
                <span className="truncate">{breadcrumbLabel}</span>
              </div>
            )}
          </DialogHeader>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className="grid gap-6 overflow-y-auto py-2 pr-2">
              {/* Allowed file types for refinement (gate) */}
              <div className="grid gap-2">
                <Label htmlFor="file_types">Allowed file types for refinement</Label>
                <Input
                  id="file_types"
                  value={fileTypesInput}
                  onChange={(e) => setFileTypesInput(e.target.value)}
                  placeholder="ALL or e.g. pdf, xlsx, docx"
                  disabled={disabled}
                />
                <p className="text-xs text-muted-foreground">
                  Controls <span className="font-medium">whether</span> refinement happens. Use{" "}
                  <span className="font-medium">ALL</span> to run refinement for any extension, or
                  provide a comma-separated list.
                </p>
              </div>

              {/* File Type Forks Section */}
              <Collapsible open={forksExpanded} onOpenChange={setForksExpanded}>
                <div className="flex items-center justify-between">
                  <Label className="text-base">File Type Forks</Label>
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" size="sm">
                      {forksExpanded ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </Button>
                  </CollapsibleTrigger>
                </div>
                <p className="mb-2 text-xs text-muted-foreground">
                  Controls <span className="font-medium">which</span> subcategories are used based
                  on file type. Different file types can be refined into different subcategory sets.
                  If no forks are configured, all subcategories are used.
                </p>
                <CollapsibleContent className="space-y-3">
                  {forks.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      No file type forks configured. All subcategories will be used for refinement.
                    </p>
                  ) : (
                    forks.map((fork, idx) => (
                      <ForkCard
                        key={idx}
                        fork={fork}
                        index={idx}
                        availableSubcategories={availableForForks}
                        onUpdate={(updates) => updateFork(idx, updates)}
                        onRemove={() => removeFork(idx)}
                        onToggleSubcategory={(subId) => toggleForkSubcategory(idx, subId)}
                        disabled={disabled}
                      />
                    ))
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={addFork}
                    disabled={disabled}
                    className="w-full"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add File Type Fork
                  </Button>
                </CollapsibleContent>
              </Collapsible>

              {/* Direct Subcategories Section */}
              <div className="grid gap-2">
                <Label>Direct Subcategories</Label>
                <div className="space-y-3">
                  {subcategories.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No subcategories configured.</p>
                  ) : (
                    subcategories.map((sc, idx) => (
                      <div key={idx} className="grid grid-cols-12 gap-2">
                        <div className="col-span-4">
                          <Input
                            value={sc.category_name}
                            onChange={(e) => {
                              const value = e.target.value;
                              setSubcategories((prev) =>
                                prev.map((p, i) => (i === idx ? { ...p, category_name: value } : p))
                              );
                            }}
                            placeholder="Subcategory name"
                            disabled={disabled}
                          />
                        </div>
                        <div className="col-span-6">
                          <Input
                            value={sc.description ?? ""}
                            onChange={(e) => {
                              const value = e.target.value;
                              setSubcategories((prev) =>
                                prev.map((p, i) =>
                                  i === idx ? { ...p, description: value.trim() ? value : null } : p
                                )
                              );
                            }}
                            placeholder="Optional description"
                            disabled={disabled}
                          />
                        </div>
                        <div className="col-span-2 flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              typeof sc.id === "number" && handleManageSubcategory(sc.id)
                            }
                            disabled={disabled || typeof sc.id !== "number"}
                            className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                            title="Manage nested subcategories"
                            aria-label="Manage nested subcategories"
                          >
                            <Settings2 className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              setSubcategories((prev) => prev.filter((_, i) => i !== idx))
                            }
                            disabled={disabled}
                          >
                            ✕
                          </Button>
                        </div>
                      </div>
                    ))
                  )}

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setSubcategories((prev) => [
                          ...prev,
                          { category_name: "", description: null },
                        ])
                      }
                      disabled={disabled}
                    >
                      Add subcategory
                    </Button>
                    <Popover open={addExistingPopoverOpen} onOpenChange={setAddExistingPopoverOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={disabled || availableForAdopting.length === 0}
                        >
                          <Plus className="mr-2 h-4 w-4" />
                          Link existing
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[300px] p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Search categories..." />
                          <CommandList>
                            <CommandEmpty>No categories found.</CommandEmpty>
                            <CommandGroup>
                              {availableForAdopting.map((cat) => (
                                <CommandItem
                                  key={cat.id}
                                  value={cat.category_name}
                                  onSelect={() => {
                                    setSubcategories((prev) => [
                                      ...prev,
                                      {
                                        id: cat.id,
                                        category_name: cat.category_name,
                                        description: cat.description ?? null,
                                        category_type: cat.category_type,
                                      },
                                    ]);
                                    setAddExistingPopoverOpen(false);
                                  }}
                                >
                                  {cat.category_name}
                                  {cat.parent_category_id && (
                                    <span className="ml-2 text-xs text-muted-foreground">
                                      (child)
                                    </span>
                                  )}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  These are real Document Categories linked under the parent. The first-pass
                  classifier sees only top-level categories, so subcategories participate only in
                  the second-pass refinement.
                </p>
                <p className="text-xs text-muted-foreground">
                  Tip: use <span className="font-medium">Manage</span> to configure nested
                  subcategories, sharing, and forks for a child category.
                </p>
              </div>

              {/* Shared Subcategories Section */}
              <div className="grid gap-2">
                <Label>Shared Subcategories</Label>
                <p className="mb-2 text-xs text-muted-foreground">
                  Borrow subcategories from other parent categories for deeper classification.
                </p>

                {availableForSharing.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No subcategories from other categories available to share.
                  </p>
                ) : (
                  <div className="space-y-2">
                    <Popover open={sharedPopoverOpen} onOpenChange={setSharedPopoverOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={sharedPopoverOpen}
                          className="w-full justify-between"
                          disabled={disabled}
                        >
                          {sharedSubcategoryIds.length > 0
                            ? `${sharedSubcategoryIds.length} shared subcategories selected`
                            : "Select subcategories to share..."}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[400px] p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Search subcategories..." />
                          <CommandList>
                            <CommandEmpty>No subcategories found.</CommandEmpty>
                            <CommandGroup>
                              {availableForSharing.map((sub) => (
                                <CommandItem
                                  key={sub.id}
                                  value={getSubcategoryLabel(sub)}
                                  onSelect={() => toggleSharedSubcategory(sub.id)}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      sharedSubcategoryIds.includes(sub.id)
                                        ? "opacity-100"
                                        : "opacity-0"
                                    )}
                                  />
                                  <span>{sub.category_name}</span>
                                  {sub.parent_name && (
                                    <span className="ml-2 text-xs text-muted-foreground">
                                      from {sub.parent_name}
                                    </span>
                                  )}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>

                    {/* Display selected shared subcategories as badges */}
                    {sharedSubcategoryIds.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {sharedSubcategoryIds.map((id) => {
                          const sub = availableForSharing.find((s) => s.id === id);
                          if (!sub) return null;
                          return (
                            <Badge
                              key={id}
                              variant="secondary"
                              className="cursor-pointer hover:bg-destructive hover:text-destructive-foreground"
                              onClick={() => !disabled && toggleSharedSubcategory(id)}
                            >
                              {sub.category_name}
                              {sub.parent_name && (
                                <span className="ml-1 opacity-70">({sub.parent_name})</span>
                              )}
                              <span className="ml-1">×</span>
                            </Badge>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={disabled}>
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </ComponentErrorBoundary>
    </Dialog>
  );
}

// Fork Card Component
interface ForkCardProps {
  fork: EditableFork;
  index: number;
  availableSubcategories: SubcategoryOption[];
  onUpdate: (
    updates: Partial<
      Pick<EditableFork, "fork_name" | "file_types" | "priority" | "subcategory_ids">
    >
  ) => void;
  onRemove: () => void;
  onToggleSubcategory: (subcategoryId: number) => void;
  disabled: boolean;
}

function ForkCard({
  fork,
  index,
  availableSubcategories,
  onUpdate,
  onRemove,
  onToggleSubcategory,
  disabled,
}: ForkCardProps) {
  const [subcategoryPopoverOpen, setSubcategoryPopoverOpen] = useState(false);

  return (
    <Card className="border-dashed">
      <CardHeader className="pb-3 pt-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 space-y-2">
            <Input
              value={fork.fork_name}
              onChange={(e) => onUpdate({ fork_name: e.target.value })}
              placeholder={`Fork ${index + 1} name (e.g., PDF Documents)`}
              className="h-8 font-medium"
              disabled={disabled}
            />
            <div className="flex gap-2">
              <div className="flex-1">
                <Input
                  value={formatFileTypes(fork.file_types)}
                  onChange={(e) => onUpdate({ file_types: parseFileTypes(e.target.value) })}
                  placeholder="File types (e.g., pdf, docx)"
                  className="h-8 text-sm"
                  disabled={disabled}
                />
              </div>
              <div className="w-20">
                <Input
                  type="number"
                  value={fork.priority}
                  onChange={(e) => onUpdate({ priority: parseInt(e.target.value) || 100 })}
                  placeholder="Priority"
                  className="h-8 text-sm"
                  disabled={disabled}
                  title="Priority (lower = higher priority)"
                />
              </div>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onRemove}
            disabled={disabled}
            className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground">Subcategories for this fork:</Label>
          <Popover open={subcategoryPopoverOpen} onOpenChange={setSubcategoryPopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                className="w-full justify-between text-sm"
                disabled={disabled || availableSubcategories.length === 0}
              >
                {fork.subcategory_ids.length > 0
                  ? `${fork.subcategory_ids.length} subcategories selected`
                  : "Select subcategories..."}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[350px] p-0" align="start">
              <Command>
                <CommandInput placeholder="Search subcategories..." />
                <CommandList>
                  <CommandEmpty>No subcategories available.</CommandEmpty>
                  <CommandGroup>
                    {availableSubcategories.map((sub) => (
                      <CommandItem
                        key={sub.id}
                        value={sub.category_name}
                        onSelect={() => onToggleSubcategory(sub.id)}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            fork.subcategory_ids.includes(sub.id) ? "opacity-100" : "opacity-0"
                          )}
                        />
                        <span>{sub.category_name}</span>
                        {sub.parent_name && (
                          <span className="ml-2 text-xs text-muted-foreground">
                            from {sub.parent_name}
                          </span>
                        )}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

          {/* Display selected subcategories as badges */}
          {fork.subcategory_ids.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {fork.subcategory_ids.map((id) => {
                const sub = availableSubcategories.find((s) => s.id === id);
                if (!sub) return null;
                return (
                  <Badge
                    key={id}
                    variant="outline"
                    className="cursor-pointer text-xs hover:bg-destructive hover:text-destructive-foreground"
                    onClick={() => !disabled && onToggleSubcategory(id)}
                  >
                    {sub.category_name}
                    <span className="ml-1">×</span>
                  </Badge>
                );
              })}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
